import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import {
  Box,
  Typography,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress,
  Tooltip,
  TextField,
  Button,
  Alert,
  Chip,
  Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { customGetAPI, customAPI } from '../../services/utils/CustomAPI';
import { Loader } from '../../components/Loader';
import {
  checkEditorPermission,
  checkViewerPermission,
  getAdministrationRedirectPath,
  isProvisioningLoaded,
} from '../../services/utils/HelperFunction';

const selectDomain = (state) => state.tokenReducer.domain;
const selectUserScreenAccess = (state) => state.tokenReducer.userScreenAccess;

const FEATURE_LABELS = {
  attendance: 'Attendance',
  leave: 'Leave management',
  shifts: 'Shift management',
  tasks: 'Tasks & projects',
  crm: 'CRM',
  payroll: 'Payroll',
  system: 'System',
};

const CHANNEL_LABELS = {
  push: 'Push',
  bell: 'In-app',
  email: 'Email',
};

const NotificationSettings = () => {
  const domain = useSelector(selectDomain);
  const userScreenAccess = useSelector(selectUserScreenAccess);
  const moduleDetails = userScreenAccess?.module_details;
  // The sidebar row only shows for a role whose provisioning payload grants
  // viewer on this submodule (ADMINISTRATION_VIEWER_GATED_MODULES in
  // Sidebar/index.js), but that only hides the menu row -- there's no
  // per-route access guard anywhere in the app (Admin.js maps every route
  // straight to its component), so a refresh while already on this screen,
  // a bookmark, or browser back/forward still rendered it fully with no
  // check at all. hasViewAccess below closes that; check_access separately
  // gates the actual save action ("block the mutating action, don't disable
  // every control", the same pattern EODSettings/RoleAndUserGroup use for
  // editor permission) since a viewer-only role should still see this
  // screen, just not be able to save changes to it.
  const hasViewAccess = checkViewerPermission(
    moduleDetails,
    'Notification Settings',
    'Notification Settings',
  );
  const check_access = checkEditorPermission(
    moduleDetails,
    'Notification Settings',
    'Notification Settings',
  );

  const history = useHistory();
  // Redux's userScreenAccess starts out as last session's cached value (or
  // the empty array default) until this page's own provisioning fetch
  // resolves -- see isProvisioningLoaded. Without this gate, a hard refresh
  // redirected away immediately off that stale/empty snapshot, before the
  // real fetch had a chance to answer, which looked like the provisioning
  // API was never even called.
  const provisioningLoaded = isProvisioningLoaded(userScreenAccess, "Administration");

  // A static "Access Restricted" message was a dead end -- send the user
  // somewhere they can actually see instead (another Administration screen
  // first, then the same "first accessible product" fallback the login flow
  // already uses). Runs as an effect, not during render, since navigating
  // is a side effect.
  useEffect(() => {
    if (!provisioningLoaded || hasViewAccess) return;
    history.replace(getAdministrationRedirectPath(moduleDetails));
  }, [provisioningLoaded, hasViewAccess, moduleDetails, history]);

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [tenantEdits, setTenantEdits] = useState({});
  const [eventEdits, setEventEdits] = useState({});

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await customGetAPI(`${domain}v1/notifications/config/`);
      setConfig(res?.responseDetails);
    } catch (e) {
      setError('Failed to load notification settings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [domain]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const getTenantValue = (field) =>
    tenantEdits[field] !== undefined
      ? tenantEdits[field]
      : config?.tenant?.[field];

  const getEventValue = (event_type, channel_key) => {
    const editKey = `${channel_key}_enabled`;
    if (eventEdits[event_type]?.[editKey] !== undefined) {
      return eventEdits[event_type][editKey];
    }
    for (const events of Object.values(config?.events_by_feature || {})) {
      const ev = events.find((e) => e.event_type === event_type);
      if (ev) return ev.tenant[editKey];
    }
    return true;
  };

  const updateTenant = (field, value) =>
    setTenantEdits((prev) => ({ ...prev, [field]: value }));

  const updateEvent = (event_type, channel_key, value) =>
    setEventEdits((prev) => ({
      ...prev,
      [event_type]: {
        ...(prev[event_type] || {}),
        [`${channel_key}_enabled`]: value,
      },
    }));

  const handleSave = async () => {
    if (!check_access) {
      setError(
        'Access denied: You are not authorized to update notification settings.',
      );
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (Object.keys(tenantEdits).length > 0) {
        await customAPI(
          `${domain}v1/notifications/config/tenant/`,
          'PUT',
          { ...config.tenant, ...tenantEdits },
        );
      }
      const eventUpdates = Object.entries(eventEdits).map(
        ([event_type, channels]) => ({ event_type, ...channels }),
      );
      if (eventUpdates.length > 0) {
        await customAPI(
          `${domain}v1/notifications/config/events/`,
          'PUT',
          { events: eventUpdates },
        );
      }
      setSaved(true);
      setTenantEdits({});
      setEventEdits({});
      setTimeout(() => setSaved(false), 3000);
      await loadConfig();
    } catch (e) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setTenantEdits({});
    setEventEdits({});
    setError('');
  };

  const hasPendingChanges =
    Object.keys(tenantEdits).length > 0 || Object.keys(eventEdits).length > 0;

  if (!provisioningLoaded) {
    return <Loader info="Loading..." />;
  }

  if (!hasViewAccess) {
    return <Loader info="Redirecting..." />;
  }

  if (loading) {
    return <Loader info="Loading..." />;
  }

  const masterEnabled = getTenantValue('notifications_enabled');

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', py: 3, px: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" fontWeight={500} gutterBottom>
        Notification Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Control which notifications OrganFlow sends to your team.
        Changes take effect within 5 minutes.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Settings saved successfully.
        </Alert>
      )}

      {/* Master switch */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={500}>
            Enable notifications
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Master switch — disabling stops ALL notifications for your
            organisation immediately.
          </Typography>
        </Box>
        <Switch
          checked={!!masterEnabled}
          onChange={(e) =>
            updateTenant('notifications_enabled', e.target.checked)
          }
          color="primary"
        />
      </Box>

      {/* Feature toggles */}
      <Typography
        variant="overline"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        Modules
      </Typography>
      <Box
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Grid container spacing={2}>
          {Object.entries(FEATURE_LABELS).map(([feature, label]) => (
            <Grid item xs={12} sm={6} key={feature}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="body2">{label}</Typography>
                <Tooltip
                  title={!masterEnabled ? 'Enable notifications first' : ''}
                >
                  <span>
                    <Switch
                      checked={
                        !!masterEnabled && !!getTenantValue(`feature_${feature}`)
                      }
                      onChange={(e) =>
                        updateTenant(`feature_${feature}`, e.target.checked)
                      }
                      disabled={!masterEnabled}
                      size="small"
                    />
                  </span>
                </Tooltip>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Channels */}
      <Typography
        variant="overline"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        Delivery channels
      </Typography>
      <Box
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        {[
          { field: 'channel_push_enabled', label: 'Push notifications (mobile)' },
          { field: 'channel_bell_enabled', label: 'In-app notification bell' },
          { field: 'channel_email_enabled', label: 'Email digests' },
        ].map(({ field, label }) => (
          <Box
            key={field}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 0.5,
            }}
          >
            <Typography variant="body2">{label}</Typography>
            <Tooltip title={!masterEnabled ? 'Enable notifications first' : ''}>
              <span>
                <Switch
                  checked={!!masterEnabled && !!getTenantValue(field)}
                  onChange={(e) => updateTenant(field, e.target.checked)}
                  disabled={!masterEnabled}
                  size="small"
                />
              </span>
            </Tooltip>
          </Box>
        ))}
      </Box>

      {/* Approval routing */}
      <Typography
        variant="overline"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        Approval notifications
      </Typography>
      <Box
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                Copy Super Admin on all approvals
              </Typography>
              {!!getTenantValue('notify_super_admin_on_approvals') && (
                <Chip label="ON" size="small" color="info" sx={{ height: 18, fontSize: 10 }} />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {getTenantValue('notify_super_admin_on_approvals')
                ? 'Super Admin receives a copy of every approval notification in addition to the Department Lead.'
                : 'Only the relevant Department Lead receives approval notifications. Super Admin is notified only when no Department Lead is configured (fallback).'}
            </Typography>
          </Box>
          <Tooltip title={!masterEnabled ? 'Enable notifications first' : ''}>
            <span>
              <Switch
                checked={!!masterEnabled && !!getTenantValue('notify_super_admin_on_approvals')}
                onChange={(e) =>
                  updateTenant('notify_super_admin_on_approvals', e.target.checked)
                }
                disabled={!masterEnabled}
                size="small"
              />
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Escalation */}
      <Typography
        variant="overline"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        Escalation reminders
      </Typography>
      <Box
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              Enable escalation reminders
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Send a reminder to the approver if a request remains pending beyond the threshold.
            </Typography>
          </Box>
          <Switch
            checked={!!masterEnabled && !!getTenantValue('escalation_enabled')}
            onChange={(e) => updateTenant('escalation_enabled', e.target.checked)}
            disabled={!masterEnabled}
          />
        </Box>
        {!!getTenantValue('escalation_enabled') && (
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Reminder threshold (hours)"
              type="number"
              size="small"
              value={getTenantValue('escalation_hours') ?? 24}
              onChange={(e) =>
                updateTenant('escalation_hours', parseInt(e.target.value, 10) || 24)
              }
              disabled={!masterEnabled}
              inputProps={{ min: 1, max: 168 }}
              sx={{ width: 220 }}
              helperText="Approver reminded after this many hours of no action"
            />
          </Box>
        )}
      </Box>

      {/* Missing punch thresholds */}
      <Typography
        variant="overline"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        Missing punch escalation
      </Typography>
      <Box
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" fontWeight={500}>
            Enable missing punch email alerts
          </Typography>
          <Switch
            checked={
              !!masterEnabled &&
              !!getTenantValue('feature_attendance') &&
              !!getTenantValue('missing_punch_alerts_enabled')
            }
            onChange={(e) =>
              updateTenant('missing_punch_alerts_enabled', e.target.checked)
            }
            disabled={!masterEnabled || !getTenantValue('feature_attendance')}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Employee reminder (days)"
            type="number"
            size="small"
            value={getTenantValue('missing_punch_employee_alert_days') ?? 2}
            onChange={(e) =>
              updateTenant(
                'missing_punch_employee_alert_days',
                parseInt(e.target.value, 10) || 2,
              )
            }
            disabled={!getTenantValue('missing_punch_alerts_enabled')}
            inputProps={{ min: 1, max: 30 }}
            sx={{ width: 210 }}
            helperText="Days before employee is emailed"
          />
          <TextField
            label="Manager escalation (days)"
            type="number"
            size="small"
            value={getTenantValue('missing_punch_manager_escalation_days') ?? 7}
            onChange={(e) =>
              updateTenant(
                'missing_punch_manager_escalation_days',
                parseInt(e.target.value, 10) || 7,
              )
            }
            disabled={!getTenantValue('missing_punch_alerts_enabled')}
            inputProps={{ min: 1, max: 60 }}
            sx={{ width: 230 }}
            helperText="Days before manager is notified"
          />
        </Box>
      </Box>

      {/* Per-event configuration */}
      <Typography
        variant="overline"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        Per-event configuration
      </Typography>

      {config?.events_by_feature &&
        Object.entries(config.events_by_feature).map(([feature, events]) => {
          const featureEnabled =
            !!masterEnabled && !!getTenantValue(`feature_${feature}`);

          return (
            <Accordion
              key={feature}
              disableGutters
              sx={{ mb: 1, border: '1px solid', borderColor: 'divider' }}
              elevation={0}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    pr: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {FEATURE_LABELS[feature] || feature}
                  </Typography>
                  {!featureEnabled && (
                    <Chip label="disabled" size="small" variant="outlined" />
                  )}
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ ml: 'auto' }}
                  >
                    Push · Bell · Email
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {events.map((ev, idx) => (
                  <React.Fragment key={ev.event_type}>
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 2,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {ev.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {ev.description}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            flexShrink: 0,
                            alignItems: 'center',
                          }}
                        >
                          {['push', 'bell', 'email'].map((ch) => (
                            <Tooltip
                              key={ch}
                              title={
                                !featureEnabled
                                  ? 'Feature disabled'
                                  : CHANNEL_LABELS[ch]
                              }
                            >
                              <span>
                                <Switch
                                  size="small"
                                  checked={
                                    featureEnabled &&
                                    getEventValue(ev.event_type, ch)
                                  }
                                  onChange={(e) =>
                                    updateEvent(ev.event_type, ch, e.target.checked)
                                  }
                                  disabled={!featureEnabled}
                                />
                              </span>
                            </Tooltip>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                    {idx < events.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}

      {/* Save / Reset bar -- hidden rather than shown-and-blocked for a
          viewer-only role, same treatment as the header create actions
          elsewhere in Administration. */}
      {check_access && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 3,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.default',
            pb: 1,
          }}
        >
          <Button
            onClick={handleReset}
            disabled={saving || !hasPendingChanges}
            variant="outlined"
          >
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasPendingChanges}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Saving...' : 'Save settings'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default NotificationSettings;
